import { c as _c } from "react/compiler-runtime";
import type { DocumentUpload } from '@onefootprint/request-types/dashboard';
import { cx } from 'class-variance-authority';
import { AnimatePresence } from 'framer-motion';
import groupBy from 'lodash/groupBy';
import partition from 'lodash/partition';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type useEntityVault from '@/entities/hooks/use-entity-vault';
import type { TransformedDocument } from '@/entity/hooks/use-documents';
import useSession from '@/hooks/use-session';
import { type Rotation, rotateClockwise } from '@/utils/rotation';
import findVisibleElement from '../../utils/find-visible-element';
import { getUploadKey } from '../../utils/get-doc-upload-key';
import showSelfiePreview, { getSelfieIndex } from '../../utils/show-selfie-preview';
import DetailsLayoutWrapper from '../details-layout-wrapper';
import DrawerContent from '../drawer-content';
import FailedUploads from '../failed-uploads';
import ReferenceImage from '../reference-image';
import SelfiePreview from '../selfie-preview';
import TableOfContents from '../table-of-contents';
import UploadImageItem from '../upload-image-item';
export type DecryptedDocumentContentProps = {
  document: TransformedDocument;
  vault: ReturnType<typeof useEntityVault>;
};
const DecryptedDocumentContent = t0 => {
  const $ = _c(44);
  const {
    document,
    vault
  } = t0;
  const {
    data: session
  } = useSession();
  let t1;
  if ($[0] !== vault.data?.vault) {
    t1 = vault.data?.vault || {};
    $[0] = vault.data?.vault;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const vaultData = t1;
  const {
    uploads
  } = document;
  const referenceComparison = session.user?.isFirmEmployee ? document.faceComparison : undefined;
  const latestFront = uploads.find(_temp);
  const latestBack = uploads.find(_temp2);
  const latestSelfie = uploads[getSelfieIndex(uploads)];
  const latestUploads = [latestFront, latestBack, latestSelfie].filter(Boolean) as (DocumentUpload & {
    isLatest: boolean;
  })[];
  const remainingUploads = uploads.filter(u_1 => !latestUploads.includes(u_1));
  const [successfulUploads, failedUploads] = partition(remainingUploads, _temp3);
  const latestAndSuccessfulUploads = [...latestUploads, ...successfulUploads];
  const groupedFailedUploads = Object.values(groupBy(failedUploads, "side"));
  const sortedAllUploads = [...latestAndSuccessfulUploads, ...groupedFailedUploads.flat()];
  const referenceIndex = latestAndSuccessfulUploads.length + groupedFailedUploads.length;
  const scrollContainerRef = useRef(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const uploadRefs = useRef(t2);
  const [visibleUploadIndex, setVisibleUploadIndex] = useState(0);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [rotations, setRotations] = useState(t3);
  const isSelfiePreviewShown = showSelfiePreview(sortedAllUploads, visibleUploadIndex);
  let t4;
  if ($[4] !== setVisibleUploadIndex) {
    t4 = () => {
      setTimeout(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) {
          return;
        }
        const updateVisibleUpload = () => {
          const containerRect = scrollContainer.getBoundingClientRect();
          const visibleIndex = findVisibleElement(containerRect, uploadRefs.current);
          if (visibleIndex !== -1) {
            setVisibleUploadIndex(visibleIndex);
          }
        };
        scrollContainer.addEventListener("scroll", updateVisibleUpload);
        updateVisibleUpload();
        return () => {
          scrollContainer.removeEventListener("scroll", updateVisibleUpload);
        };
      }, 300);
    };
    $[4] = setVisibleUploadIndex;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = index => {
      const target = uploadRefs.current[index];
      if (target) {
        target.scrollIntoView({
          behavior: "smooth"
        });
      }
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const scrollToUpload = t6;
  let t7;
  if ($[8] !== setRotations) {
    t7 = uploadKey => {
      setRotations(prev => ({
        ...prev,
        [uploadKey]: rotateClockwise(prev[uploadKey] ?? 0)
      }));
    };
    $[8] = setRotations;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const rotateUpload = t7;
  if (document.redactedAt) {
    let t8;
    if ($[10] !== document || $[11] !== vault) {
      t8 = <DrawerContent document={document} vault={vault} />;
      $[10] = document;
      $[11] = vault;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <RedactedImages />;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    let t10;
    if ($[14] !== t8) {
      t10 = <DetailsLayoutWrapper drawerChildren={t8}>{t9}</DetailsLayoutWrapper>;
      $[14] = t8;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    return t10;
  }
  let t8;
  if ($[16] !== document || $[17] !== vault) {
    t8 = <DrawerContent document={document} vault={vault} />;
    $[16] = document;
    $[17] = vault;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = uploads.length > 1;
  let t10;
  if ($[19] !== t9) {
    t10 = cx("flex flex-col items-center gap-8 w-full md:w-[70%] h-full", {
      "[&>*:last-child]:pb-6": t9
    });
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = latestAndSuccessfulUploads.map((upload_0, index_0) => {
    const uploadKey_0 = getUploadKey(upload_0);
    return <UploadImageItem key={uploadKey_0} upload={upload_0} vault={vaultData} rotation={rotations[uploadKey_0] ?? 0} onRotate={() => rotateUpload(uploadKey_0)} ref={el => {
      uploadRefs.current[index_0] = el;
    }} />;
  });
  const t12 = failedUploads.length > 0 && groupedFailedUploads.map((sameSideUploads, index_1) => <FailedUploads key={getUploadKey(sameSideUploads[0])} uploads={sameSideUploads} vault={vaultData} rotations={rotations} onRotate={rotateUpload} ref={el_0 => {
    uploadRefs.current[latestAndSuccessfulUploads.length + index_1] = el_0;
  }} />);
  let t13;
  if ($[21] !== referenceComparison || $[22] !== referenceIndex || $[23] !== vault) {
    t13 = referenceComparison && <ReferenceImage comparison={referenceComparison} vault={vault} ref={el_1 => {
      uploadRefs.current[referenceIndex] = el_1;
    }} />;
    $[21] = referenceComparison;
    $[22] = referenceIndex;
    $[23] = vault;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t12 || $[28] !== t13) {
    t14 = <div className={t10}>{t11}{t12}{t13}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== isSelfiePreviewShown || $[31] !== latestSelfie || $[32] !== rotations || $[33] !== vaultData) {
    t15 = isSelfiePreviewShown && <SelfiePreview key="selfie" image={latestSelfie.image} vault={vaultData} rotation={rotations[getUploadKey(latestSelfie)] ?? 0} />;
    $[30] = isSelfiePreviewShown;
    $[31] = latestSelfie;
    $[32] = rotations;
    $[33] = vaultData;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== isSelfiePreviewShown || $[36] !== referenceComparison || $[37] !== referenceIndex || $[38] !== vaultData || $[39] !== visibleUploadIndex) {
    t16 = referenceComparison && !isSelfiePreviewShown && visibleUploadIndex !== referenceIndex && <SelfiePreview key="reference" image={referenceComparison.reference} vault={vaultData} />;
    $[35] = isSelfiePreviewShown;
    $[36] = referenceComparison;
    $[37] = referenceIndex;
    $[38] = vaultData;
    $[39] = visibleUploadIndex;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t15 || $[42] !== t16) {
    t17 = <AnimatePresence>{t15}{t16}</AnimatePresence>;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  return <DetailsLayoutWrapper drawerChildren={t8} ref={scrollContainerRef}><TableOfContents latestAndSuccessfulUploads={latestAndSuccessfulUploads} failedUploads={groupedFailedUploads} onClick={scrollToUpload} visibleIndex={visibleUploadIndex} hasReference={!!referenceComparison} />{t14}{t17}</DetailsLayoutWrapper>;
};
const RedactedImages = () => {
  const $ = _c(12);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "fieldset.documents.details.redacted"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t0);
  let t1;
  if ($[1] !== t) {
    t1 = t(_temp4);
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <p className="text-label-2 text-primary">{t1}</p>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t(_temp5);
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <p className="text-body-3 text-tertiary">{t3}</p>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <div className="flex flex-col items-center justify-center gap-1 w-full h-full text-center">{t2}{t4}</div>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
export default DecryptedDocumentContent;
function _temp(u) {
  return u.side === "front" && u.isLatest;
}
function _temp2(u_0) {
  return u_0.side === "back" && u_0.isLatest;
}
function _temp3(upload) {
  return upload.failureReasons.length === 0;
}
function _temp4($) {
  return $.title;
}
function _temp5($_0) {
  return $_0.description;
}